import type { GuidedSetupStep } from './guided-setup-steps';

/**
 * The five product tours added by Phase 2 of the guided-onboarding plan (G5, Q5).
 *
 * ## What these are for, and what they are not
 *
 * Each one answers "what is this screen for, and what do I do first" for a surface a genuinely
 * new user lands on and cannot decode. Three to five steps, no more: a tour long enough to need
 * a progress bar is documentation, and documentation belongs in `/document`.
 *
 * **Caveat C8 applies to every line of copy here: no step may assert a state the platform cannot
 * observe.** Where the honest answer is "we cannot tell you from here", the step says so and
 * points at the control that can. There is no `status` field on `GuidedSetupStep` and none may be
 * added.
 *
 * ## Anchors
 *
 * Every `anchor` is the value of a `data-tour-step` ATTRIBUTE on a real element — never a CSS
 * selector (see {@link GuidedSetupStep.anchor}). An anchor that is absent from the DOM makes its
 * step vanish rather than point at nothing, which is why a tour whose anchors render after an
 * async load still reads correctly: it simply has fewer steps until the data arrives, and
 * `GuidedTourHostComponent` waits for the anchors before auto-opening.
 *
 * ## Ordering
 *
 * Q5's priority order — workspace shell, exploit hunt, SBOM/inventory, threat modelling,
 * policies — is a REASONED order, not a measured one. There is no telemetry on where new users
 * get stuck, and this file does not pretend otherwise.
 */

// ── Workspace shell (`/workspace/:id`) ──────────────────────────────────────────────────────

/** Tour id — must match `UserTourController.ALLOWED_TOUR_IDS`. */
export const WORKSPACE_TOUR_ID = 'workspace';

export const WORKSPACE_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'ws-intro',
    title: 'This is a workspace',
    anchor: null,
    body: [
      'A workspace is a set of repositories analysed together. Cross-repository calls, shared ' +
        'libraries and taint paths that leave one repo and land in another are only visible ' +
        'inside one — which is why the unit of work here is a workspace and not a repository.',
      'Everything on this screen is scoped to the workspace named in the picker at the top left.',
    ],
  },
  {
    id: 'ws-scope',
    title: 'Switch workspace, or add a repository',
    anchor: 'ws-scope',
    body: [
      'This picker changes which workspace you are looking at, and is also where you create one ' +
        'or add a repository to the current one.',
      'A repository added here is cloned and analysed in the background; the corner progress ' +
        'widget reports it, and the graph fills in as it finishes.',
    ],
  },
  {
    id: 'ws-graph',
    title: 'The code graph',
    anchor: 'ws-graph',
    body: [
      'Nodes are symbols — files, classes, functions — and edges are the relationships between ' +
        'them: calls, imports, inheritance. Node size follows PageRank, so the structurally ' +
        'important code is the code that looks important.',
      'A repository that has not been analysed yet contributes nothing to this view. That is an ' +
        'absence of data, not a finding of "no risk".',
    ],
  },
  {
    id: 'ws-panel',
    title: 'The side panel is where the findings live',
    anchor: 'ws-panel',
    body: [
      'The top row groups this workspace\'s views: Code (Explorer, Filters), Findings (one view ' +
        'per scanning domain — SAST, SCA, secrets, IaC, containers and more), Policies, Inventory ' +
        '(SBOM, AI-BOM) and Investigations (the exploit hunt). The row below lists the chosen ' +
        'group\'s views.',
      'Which views you see depends on what your organisation has enabled, so these rows are not ' +
        'the same for every account.',
    ],
  },
  {
    id: 'ws-chat',
    title: 'Ask about the code you are looking at',
    anchor: 'ws-chat',
    body: [
      'The assistant answers against this workspace’s graph and findings rather than against ' +
        'a general model of your language. It needs an AI provider key configured for your ' +
        'organisation; without one it will tell you so instead of guessing.',
    ],
  },
];

// ── Exploit Hunt (`/risks/exploits`) ────────────────────────────────────────────────────────

export const EXPLOITS_TOUR_ID = 'exploits';

export const EXPLOITS_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'hunt-intro',
    title: 'What an exploit hunt is',
    anchor: null,
    body: [
      'A hunt asks a model to look for exploitable paths in your code, then puts every candidate ' +
        'through two further passes: a judge that throws out what it cannot substantiate, and a ' +
        'verify pass that looks for the evidence again independently.',
      'It is a search, not a proof. A finding that survives all three passes is a strong lead ' +
        'with cited code — not a demonstrated exploit.',
    ],
  },
  {
    id: 'hunt-run',
    title: 'Starting a hunt costs money',
    anchor: 'hunt-run',
    body: [
      'This runs across every repository in scope and makes metered model calls per file, so the ' +
        'cost scales with how much code you point it at. The button states the current estimate.',
      'It needs an AI provider key configured for your organisation. Nothing starts without one.',
    ],
  },
  {
    id: 'hunt-stats',
    title: 'Read the funnel, not just the total',
    anchor: 'hunt-stats',
    body: [
      '"Hunted" is what the first pass proposed; "Verified" is what survived. The judge filter ' +
        'rate is the gap between them, and a high rate is the system working rather than failing.',
      'A hunt that finishes with nothing verified is a legitimate outcome. It does not mean the ' +
        'code is clean — only that this run found nothing it could substantiate.',
    ],
  },
  {
    id: 'hunt-findings',
    title: 'Every finding carries its evidence',
    anchor: 'hunt-findings',
    body: [
      'Open a row to see the cited file and line, the attack path the judge accepted, and the ' +
        'verify pass’s own verdict. Judge them on that evidence.',
    ],
  },
];

// ── SBOM / Inventory (`/inventory/sbom`) ────────────────────────────────────────────────────

export const SBOM_TOUR_ID = 'sbom';

export const SBOM_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'sbom-intro',
    title: 'One row per component, across every repository',
    anchor: null,
    body: [
      'This is the aggregated bill of materials: each package you depend on, wherever it appears. ' +
        'The same package at two versions is two rows, because they carry different risk.',
      'It is built from what has actually been scanned. A repository with no completed scan ' +
        'contributes nothing here, so an empty list can mean "nothing scanned yet" rather than ' +
        '"no dependencies".',
    ],
  },
  {
    id: 'sbom-scope',
    title: 'Build, container, or both',
    anchor: 'sbom-scope',
    body: [
      '"Build" is what your manifests declare. "Container" is what was actually found inside a ' +
        'built image — which routinely includes system packages no manifest mentions.',
      '"Combined" shows both and marks which source each component came from. The two rarely ' +
        'agree, and the disagreement is usually the interesting part.',
    ],
  },
  {
    id: 'sbom-view',
    title: 'Group by repository, or flatten',
    anchor: 'sbom-view',
    body: [
      'Tree groups components under the repository that pulls them in — use it to answer "what ' +
        'does this service depend on". Flat is one searchable list of every component — use it ' +
        'to answer "who uses this package".',
    ],
  },
  {
    id: 'sbom-rescan',
    title: 'Refreshing the inventory',
    anchor: 'sbom-rescan',
    body: [
      'A rescan re-derives the inventory for every workspace. It is a background job, so this ' +
        'page will not sit and wait for it; the corner progress widget reports it while it runs.',
    ],
  },
];

// ── Threat modelling (the workspace right panel) ────────────────────────────────────────────

export const THREAT_MODEL_TOUR_ID = 'threat-model';

/**
 * Threat modelling is a **tab of the workspace right panel**, not a route of its own — so per
 * `.claude/rules/new-section-onboarding-coverage.md` R1 the question was whether it should be a
 * step of the workspace tour rather than a tour. It is a tour because it has its own multi-step
 * workflow (choose a depth, run it, then read a nine-tab result) that would swamp a
 * five-step tour of the shell, and because its anchors only exist once the panel is open — so it
 * genuinely needs its own trigger point, which the workspace tour cannot provide.
 *
 * Consequence worth knowing: every anchor here is absent until the user opens that panel, so this
 * tour resolves to zero steps and refuses to open anywhere else. `GuidedTourHostComponent`'s
 * anchor watcher is what lets it auto-open at the moment the panel appears rather than never.
 */
export const THREAT_MODEL_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'tm-run',
    title: 'Threat modelling runs on demand',
    anchor: 'tm-run',
    body: [
      'This reads the workspace’s own graph and findings and produces a threat model for it — ' +
        'entry points, trust boundaries, the attack surface each one exposes.',
      'It is a metered model run, so it happens when you ask for it and not on a schedule.',
    ],
  },
  {
    id: 'tm-mode',
    title: 'Simple or Deep',
    anchor: 'tm-mode',
    body: [
      'Simple is one pass over the summarised context — quick, cheap, and enough to see the shape ' +
        'of the system. Deep re-reads the graph per component and costs proportionally more.',
      'Start with Simple. If the result comes back sparse, the page says so rather than presenting ' +
        'a thin model as a complete one, and that is your signal to try Deep.',
    ],
  },
  {
    id: 'tm-tabs',
    title: 'The result is nine views of one model',
    anchor: 'tm-tabs',
    body: [
      'Overview is the roll-up. Threats, Attack Paths and Targets are the model itself; Context ' +
        'and Architecture are what it was derived from; Full Model is the whole document.',
      'Full Model is fetched when you open it, not up front — so it costs a request rather than ' +
        'loading with the tab row.',
    ],
  },
];

// ── Policies (`/admin/policies`) ────────────────────────────────────────────────────────────

export const POLICIES_TOUR_ID = 'policies';

export const POLICIES_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'policy-intro',
    title: 'A policy decides what a scan does',
    anchor: null,
    body: [
      'Which scanners run, at what depth, and what result blocks a pull request. A repository ' +
        'with no policy attached gets the defaults, which are deliberately conservative.',
    ],
  },
  {
    id: 'policy-templates',
    title: 'Create a policy',
    anchor: 'policy-templates',
    body: [
      'Name the policy, choose the scan depth its pull requests get — Standard runs pattern ' +
        'rules and then graph triage over the changed files; Pattern only skips the triage — and ' +
        'pick the scopes it governs. One button creates one policy per scope.',
      'A new policy has no rules and no default verdict: it expresses no opinion until you add ' +
        'rules, and the built-in severity rules decide in the meantime.',
    ],
  },
  {
    id: 'policy-list',
    title: 'System templates and your own policies',
    anchor: 'policy-list',
    body: [
      'System templates are read-only and maintained by Phoenix. Your own policies sit below ' +
        'them and are the ones that actually apply to your repositories.',
    ],
  },
  {
    id: 'policy-create',
    title: 'Building one from scratch',
    anchor: 'policy-create',
    body: [
      'Only worth it when no template is close. A policy that enables a scanner your ' +
        'organisation has not licensed will run everything else and report that domain as ' +
        'skipped — it will not silently report it as clean.',
    ],
  },
];
